import cx from "clsx";
import { Collection } from "kreate-common/modules/business-types";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { httpGet$GetGachaMintingStatus } from "kreate-server/commands/GetGachaMintingStatus/fetcher";
import React from "react";
import useSWR from "swr";

import ButtonGachaMinting from "./components/ButtonGachaMinting";
import styles from "./index.module.scss";
import { getMintingLimit } from "./utils";

import { useCardanoWalletApi } from "@/modules/cardano-wallet";
import { useIsExpired } from "@/modules/common-hooks/useIsExpired";

type Props = {
  className?: string;
  style?: React.CSSProperties;
  collection: Collection;
};

export default function PanelGachaMinting({
  className,
  style,
  collection,
}: Props) {
  const wallet = useCardanoWalletApi();

  const walletAddress = wallet.type === "ready" ? wallet.address : undefined;
  const collection_policyId = collection.policyId;

  const getGachaMintingStatusSWR = useSWR(
    [
      "00219a81-a954-4a1c-8c81-1e84ba22fa08",
      { walletAddress, collection_policyId },
    ],
    async () => {
      if (!collection_policyId || !walletAddress) return null;
      return await httpGet$GetGachaMintingStatus(
        "/api/v1/gacha/minting-status",
        {
          walletAddress: walletAddress,
          policyId: collection_policyId,
        }
      );
    }
  );

  const numMinted = getGachaMintingStatusSWR.data?.noMintedByYourWallet;
  const mintingLimit = getMintingLimit(collection);
  const numAvailable =
    numMinted != null && mintingLimit != null
      ? Math.max(mintingLimit - numMinted, 0)
      : undefined;
  const isStarted = useIsExpired(collection.gachaMinting?.startedAt);
  const isEnded = useIsExpired(collection.gachaMinting?.endedAt);

  if (isEnded) return null;

  return (
    <div className={cx(styles.container, className)} style={style}>
      <Flex.Row alignItems="center">
        <Flex.Cell className={styles.description} padding="0 16px">
          <span>
            <span>{`${collection.gachaMinting?.numRemainingTokens} Tokens Available`}</span>
            {numAvailable != null ? (
              <span>{` - You have ${numAvailable} more Mints`}</span>
            ) : undefined}
          </span>
        </Flex.Cell>
        <ButtonGachaMinting
          collection={collection}
          disabled={numAvailable === 0 || !isStarted}
        />
      </Flex.Row>
    </div>
  );
}
